// Connects the editor to the audio engine: keeps the engine's project in step with the loop,
// runs the transport (pattern, song, record with count-in) and turns the
// engine's `Recorded` events into notes (one undo step per recording pass).
import { GRID_TICKS, LOOP_TICKS, type KitId } from "@eb/shared";
import { engineHost, type EngineHost } from "../audio/host";
import { stopRiverIfPlaying } from "../river/context";
import { ClockModel } from "../audio/clock";
import { heldLength, insertRecorded } from "./recording";
import { addNote, sameNote } from "./ops";
import { arrangementFor, beginGesture, edit, endGesture, songKeyOf, useEditor, type PlayMode } from "./store";
import { previewLoop, useWizard } from "./wizardClient";
import { STREAM_BPM } from "../stream";

export const clockModel = new ClockModel();
let host: EngineHost | null = null;
let pushScheduled = false;
let unsubscribe: (() => void) | null = null;
let recordedStarts = new Map<string, number>();

/** Ask for the engine (creating the AudioContext; call from a user gesture where possible). */
export async function editorAudio(): Promise<EngineHost> {
  if (host) {
    void engineHost(); // resumes a suspended context inside the gesture
    return host;
  }
  const h = await engineHost();
  await h.resume();
  // The River (world mode) shares the engine: stop it so it doesn't play over the editor.
  await stopRiverIfPlaying();
  host = h;
  h.addListener({
    clock: (c) => {
      clockModel.onClock(
        {
          samplePos: c.samplePos,
          tickPos: c.tickPos,
          transportTick: c.transportTick,
          patternLength: c.patternLength,
          bpm: STREAM_BPM,
          sampleRate: h.sampleRate,
          playing: c.playing && !c.world,
          recording: c.recording,
          latencySamples: c.latencySamples,
        },
        performance.now(),
      );
      lastPatternIndex = c.patternIndex;
    },
    recorded: (pad, velocity, tick, note) => onRecorded(pad, velocity, tick, note),
    recordedRelease: (pad, tick, note) => onRecordedRelease(pad, tick, note),
  });
  const s = useEditor.getState();
  if (s.def) await ensureKit(h, s.def.kit);
  if (!unsubscribe) {
    const offEditor = useEditor.subscribe((st, prev) => {
      if (
        st.hist.present !== prev.hist.present ||
        st.spec !== prev.spec ||
        st.play !== prev.play ||
        st.pattern !== prev.pattern
      )
        schedulePush();
    });
    const offWizard = useWizard.subscribe((st, prev) => {
      if (st.suggestion !== prev.suggestion) schedulePush();
    });
    unsubscribe = () => {
      offEditor();
      offWizard();
    };
  }
  pushNow();
  return h;
}

export let lastPatternIndex = 0;

function schedulePush() {
  if (pushScheduled) return;
  pushScheduled = true;
  requestAnimationFrame(() => {
    pushScheduled = false;
    pushNow();
  });
}

/** Kits whose samples the engine has (the River may have loaded other days' kits). */
const readyKits = new Set<KitId>();

/** Load `kit` into the engine (once); resolves when its samples have been sent. */
async function ensureKit(h: EngineHost, kit: KitId): Promise<void> {
  if (readyKits.has(kit)) return;
  await h.loadKit(kit);
  readyKits.add(kit);
}

/** Send the current song to the engine right away (e.g. before previewing a changed sound). */
export function pushNow() {
  const s = useEditor.getState();
  if (!host || !s.spec) return;
  // The draft's day may use a kit the engine doesn't have yet (e.g. after listening to today's
  // River): load it first, then push — samples go through the same port, so they arrive first.
  if (s.def && !readyKits.has(s.def.kit)) {
    const kit = s.def.kit;
    void ensureKit(host, kit)
      .catch((e) => {
        // Push anyway (instruments still play); the engine reports the missing samples.
        console.warn(`[editor] kit ${kit} failed to load`, e);
        readyKits.add(kit);
      })
      .then(() => schedulePush());
    return;
  }
  const mode: PlayMode = s.play === "recording" ? "loop" : s.play;
  host.setEditor(s.spec, previewLoop(s.hist.present, s.pattern), arrangementFor(mode, s.pattern));
}

export async function startPlay(mode: Exclude<PlayMode, "stopped" | "recording">): Promise<void> {
  const h = await editorAudio();
  h.stop();
  useEditor.getState().set({ play: mode });
  pushNow();
  h.play();
}

export async function startRecording(): Promise<void> {
  const h = await editorAudio();
  const s = useEditor.getState();
  h.stop();
  s.set({ play: "recording" });
  pushNow();
  recordedStarts = new Map();
  beginGesture("Record");
  if (s.countIn) h.record();
  else {
    h.play();
    h.setRecording(true);
  }
}

export async function stopPlay(): Promise<void> {
  const h = await editorAudio();
  h.stop();
  if (useEditor.getState().play === "recording") endGesture();
  useEditor.getState().set({ play: "stopped" });
}

function onRecorded(pad: number, velocity: number, tick: number, note: number) {
  const s = useEditor.getState();
  if (s.play !== "recording" || !s.def) return;
  const q = { enabled: s.quantizeOnRecord, gridTicks: GRID_TICKS[s.grid], strength: s.quantizeStrength };
  const len = GRID_TICKS[s.grid];
  const r = insertRecorded([], pad, velocity, tick, 50, LOOP_TICKS, q, len, note);
  const ev = r.events[0];
  recordedStarts.set(`${pad}:${note}`, tick);
  const key = songKeyOf(s)!;
  edit((l) => addNote(l, ev, key));
  lastRecordedTick.set(`${pad}:${note}`, r.tick);
}

const lastRecordedTick = new Map<string, number>();

function onRecordedRelease(pad: number, tick: number, note: number) {
  const s = useEditor.getState();
  if (s.play !== "recording" || pad < 16) return;
  const k = `${pad}:${note}`;
  const start = recordedStarts.get(k);
  const at = lastRecordedTick.get(k);
  if (start === undefined || at === undefined) return;
  const len = heldLength(start, tick, LOOP_TICKS, 30);
  edit((l) => ({
    ...l,
    events: l.events.map((e) =>
      sameNote(e, { pad, note, tick: at }) ? { ...e, length_ticks: Math.min(len, LOOP_TICKS - e.tick) } : e,
    ),
  }));
}

/** Audible tick in the pattern being edited (or null if that pattern isn't what's playing). */
export function audibleLoopTick(): number | null {
  const pos = clockModel.audible(performance.now());
  if (!pos.playing || pos.countIn || lastPatternIndex !== useEditor.getState().pattern) return null;
  return pos.tick;
}
